import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/layout/AdminLayout';
import { ITeamMember } from '../types/Team';
import {
  getTeamMembers,
  deleteTeamMember as apiDeleteTeamMember,
  toggleTeamMemberActive,
  toggleTeamMemberFeatured
} from '../services/TeamService';
import { useToast } from '../hooks/useToast';
import ConfirmModal from '../components/common/ConfirmModal';
import { useConfirm } from '../hooks/useConfirm';
import styles from '../styles/Dashboard.module.css'; 
import formStyles from '../styles/FormPages.module.css';
import { PencilSquareIcon, TrashIcon, EyeIcon, EyeSlashIcon, StarIcon } from '@heroicons/react/24/outline';

const TeamPage: React.FC = () => {
  const [teamMembers, setTeamMembers] = useState<ITeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const navigate = useNavigate();
  const { isOpen, config, confirm, handleConfirm, handleCancel } = useConfirm();

  const fetchTeamMembers = async () => {
    setLoading(true);
    try {
      const fetchedMembers = await getTeamMembers();
      setTeamMembers(fetchedMembers);
    } catch (err: any) {
      setError(err.message);
      toast.error('Error', 'No se pudieron cargar los miembros.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  // --- Lógica de Permisos ---
  const userRole = localStorage.getItem('userRole') || 'observador';
  const canEdit = userRole === 'administrador' || userRole === 'editor';
  const canDelete = userRole === 'administrador';

  const checkPermission = (type: 'edit' | 'delete') => {
      if (type === 'edit' && !canDelete) {
          toast.warning('Acceso Denegado', 'Solo administradores pueden editar.');
          return false;
      }
      if (type === 'delete' && !canDelete) {
          toast.warning('Acceso Denegado', 'Solo administradores pueden eliminar.');
          return false;
      }
      return true;
  };

  // --- Handlers ---
  const handleCreate = (e: React.MouseEvent) => {
      e.preventDefault();
      if (checkPermission('edit')) navigate('/team/new');
  };

  const handleEditClick = (e: React.MouseEvent, id: number) => {
      e.preventDefault();
      if (checkPermission('edit')) navigate(`/team/edit/${id}`);
  };

  const handleDelete = async (id: number, fullName: string) => {
    if (!checkPermission('delete')) return;

    const confirmed = await confirm({
      title: 'Eliminar Miembro',
      message: `¿Eliminar a "${fullName}"?`,
      confirmText: 'Eliminar',
      type: 'danger'
    });

    if (confirmed) {
      try {
        const success = await apiDeleteTeamMember(id);
        if (success) {
          fetchTeamMembers();
          toast.success('Éxito', 'Miembro eliminado.');
        } else {
          toast.error('Error', 'No se pudo eliminar.');
        }
      } catch (err: any) {
        toast.error('Error', `Error: ${err.message}`);
      }
    }
  };

  const handleToggleActive = async (id: number, currentState: boolean) => {
    if (!checkPermission('edit')) return;
    try {
      await toggleTeamMemberActive(id, !currentState);
      setTeamMembers(prev => prev.map(m => m.id === id ? { ...m, isActive: !currentState } : m));
      toast.success('Estado actualizado', `Miembro ${!currentState ? 'activado' : 'desactivado'}.`);
    } catch (err: any) {
      toast.error('Error', 'Fallo al cambiar estado.');
    }
  };

  const handleToggleFeatured = async (id: number, currentState: boolean) => {
    if (!checkPermission('edit')) return;
    try {
      await toggleTeamMemberFeatured(id, !currentState);
      setTeamMembers(prev => prev.map(m => m.id === id ? { ...m, isFeatured: !currentState } : m));
      toast.success('Destacado actualizado', `Miembro ${!currentState ? 'destacado' : 'sin destacar'}.`);
    } catch (err: any) {
      toast.error('Error', 'Fallo al cambiar destacado.');
    }
  };

  if (loading) return <AdminLayout pageTitle="Equipo"><p>Cargando...</p></AdminLayout>;
  if (error) return <AdminLayout pageTitle="Equipo"><p style={{color:'red'}}>{error}</p></AdminLayout>;

  return (
    <AdminLayout pageTitle="Gestión del Equipo">
      <div className={formStyles.pageHeader}>
        <h2 className={formStyles.title}>Miembros del Equipo</h2>
        <button onClick={handleCreate} className={formStyles.backButton} style={{ background: '#3b82f6', border: 'none' }}>
          + Nuevo Miembro
        </button>
      </div>

      <div className={styles.recentActivity}>
        <table className={styles.projectTable}>
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Cargo</th>
              <th>Email</th>
              <th>Destacado</th>
              <th>Activo</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {teamMembers.map((member) => (
              <tr key={member.id} className={!member.isActive ? styles.inactiveMember : ''}>
                <td>
                  <div className={styles.memberInfo}>
                    {member.imageUrl && (
                      <img src={member.imageUrl} alt="Avatar" className={styles.memberAvatar} onError={(e) => (e.target as HTMLImageElement).src = 'https://via.placeholder.com/40'} />
                    )}
                    <div className={styles.memberDetails}>
                      <strong>{member.firstName} {member.lastName}</strong>
                      {!member.isActive && <div className={styles.inactiveBadge}>Inactivo</div>}
                    </div>
                  </div>
                </td>
                <td>{member.position}</td>
                <td>{member.email || '-'}</td>
                <td className={styles.featureCell}>
                  <button onClick={() => handleToggleFeatured(member.id, member.isFeatured)} className={`${styles.iconButton} ${member.isFeatured ? styles.highlighted : ''}`}>
                    <StarIcon className={styles.iconButtonIcon} />
                  </button>
                </td>
                <td className={styles.visibilityCell}>
                  <button onClick={() => handleToggleActive(member.id, member.isActive)} className={`${styles.iconButton} ${!member.isActive ? styles.hidden : ''}`}>
                    {member.isActive ? <EyeIcon className={styles.iconButtonIcon} /> : <EyeSlashIcon className={styles.iconButtonIcon} />}
                  </button>
                </td>
                <td>
                  <div className={styles.actionsCell}>
                    <button onClick={(e) => handleEditClick(e, member.id)} className={styles.editButton}>
                      <PencilSquareIcon className={styles.iconButtonIcon} />
                    </button>
                    <button onClick={() => handleDelete(member.id, `${member.firstName} ${member.lastName}`)} className={styles.deleteButton}>
                      <TrashIcon className={styles.iconButtonIcon} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ConfirmModal isOpen={isOpen} title={config?.title || ''} message={config?.message || ''} confirmText={config?.confirmText} type={config?.type} onConfirm={handleConfirm} onCancel={handleCancel} />
    </AdminLayout>
  );
};

export default TeamPage;